// src/components/SharePage.tsx
import { useState, useEffect, useRef } from "react";
import { Button } from "./ui/button";
import { Dialog, DialogContent, DialogTitle } from "./ui/dialog";
import { Input } from "./ui/input";
import { CreditCard } from "lucide-react";
import { SharetribeRepository } from "../infrastructure/repositories/SharetribeRepository";
import { apiService } from "@/services";
import { CoachCard } from "./ui/CoachCard";
import { ProductCard } from "./ui/ProductCard";
import { SwimmerCard } from "./ui/SwimmerCard";
import { FeedList } from "./ui/FeedList";
import { AddPostDialog } from "./ui/AddPostDialog";
import { Listing, IncludedItem, CreatePostForm, CoachProfileData } from "../types";
import { PostRepository } from "@/infrastructure/repositories/PostRepository";
import { MessagesPage } from "./MessagesPage";
import { ConnectedUser } from "../types/index";
import { TeamsSection } from "./TeamsSection";
import { CoachProfile } from "./CoachProfile";
import { secureStorage } from "@/utils/secureStorage";


export function SharePage({ forcedTab }: { forcedTab?: string }) {
  const tabTitles: Record<string, string> = {
    team: "Teams",
    feed: "Feed",
    messages: "Messages",
    find: "Find",
  };

  const [activeTab, setActiveTab] =
    useState<"team" | "feed" | "messages" | "find">("find");

  const [findFilter, setFindFilter] =
    useState<"coaches" | "products" | "swimmers">("coaches");

  const [listings, setListings] = useState<Listing[]>([]);
  const [included, setIncluded] = useState<IncludedItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [filterChanged, setFilterChanged] = useState(false);

  const loaderRef = useRef<HTMLDivElement | null>(null);
  const pagingLockRef = useRef(false);
  const inFlightPageRef = useRef<number | null>(null);
  const lastLoadedPageRef = useRef(0);
  const repo = new SharetribeRepository(apiService);

  const [dialogMessage, setDialogMessage] = useState("");
  const [showDialog, setShowDialog] = useState(false);

  const [showPostDialog, setShowPostDialog] = useState(false);

  const [searchQuery, setSearchQuery] = useState("");
  const [forceReloadFeed, setForceReloadFeed] = useState(0);
  const [profileOpen, setProfileOpen] = useState(false);
  const [selectedCoach, setSelectedCoach] = useState<CoachProfileData | null>(null);
  const [coachBuying, setCoachBuying] = useState(false);
  const [coachCheckoutOpen, setCoachCheckoutOpen] = useState(false);
  const [coachPaymentMethodId, setCoachPaymentMethodId] = useState<string | null>(null);
  const [coachPaymentMethod, setCoachPaymentMethod] = useState<any>(null);

  const userType = secureStorage.get("user_type") || "swimmer";
  const swimmerId = String(localStorage.getItem("current_user_swimmer_id") || "");
  const current_user_id = swimmerId;
  const [connectedIds, setConnectedIds] = useState<ConnectedUser[]>([]);

  // -------------------------------------------------------
  // Load connected list
  // -------------------------------------------------------
  useEffect(() => {
    const loadConnected = async () => {
      const userType = secureStorage.get("user_type") || "swimmer";

      let mySwimmerId = Number(localStorage.getItem("current_user_swimmer_id"));
      let myCoachId = Number(localStorage.getItem("coach_id"));

      if (userType === "coach" && !myCoachId) {
        myCoachId = mySwimmerId;
      }

      let list: any[] = [];

      if (userType === "coach") list = await repo.getCoachSwimmers(myCoachId, 2);
      else list = await repo.getSwimmerCoaches(mySwimmerId, 2);

      const connected = list.map((x) => ({
        id: String(x.id),
        coach_id: x.coach_id ? String(x.coach_id) : undefined,
        parent_swimmer_id: x.parent_swimmer_id ? String(x.parent_swimmer_id) : undefined,
      }));

      setConnectedIds(connected);
    };

    if (activeTab === "find" && findFilter === "swimmers") {
      loadConnected();
    }
  }, [activeTab, findFilter]);


  useEffect(() => {
    if (forcedTab) {
      setActiveTab(forcedTab as any);
    }
  }, [forcedTab]);


  // -------------------------------------------------------
  // Create Post
  // -------------------------------------------------------
  
  const postRepo = new PostRepository();

  const handleCreatePost = async ({ text, mediaFile }: CreatePostForm) => {
    try {
      const userId = Number(localStorage.getItem("current_user_swimmer_id")) || 0;
      const coachId = Number(localStorage.getItem("coach_id")) || 0;

      let photoUrl = "";
      let videoUrl = "";

      if (mediaFile && mediaFile.type.startsWith("image/")) {
        const res = await postRepo.uploadFeedImage(mediaFile);
        if (res.status === 1) photoUrl = res.image_link;
      }

      if (mediaFile && mediaFile.type.startsWith("video/")) {
        const res = await postRepo.uploadFeedVideo(mediaFile);
        if (res.status === 1) videoUrl = res.video_link;
      }

      const res = await postRepo.publishFeedWithResponse(
        userId,
        coachId,
        text,
        photoUrl,
        videoUrl
      );

      if (res.status === 1) {
        setShowPostDialog(false);
        setDialogMessage(res.msg || "Post published successfully.");
        setShowDialog(true);

        if (activeTab === "feed") {
          setTimeout(() => {
            window.scrollTo(0, 0);
            setForceReloadFeed((v) => v + 1);
          }, 300);
        }
      } else {
        setDialogMessage(res.msg || "Failed to publish post.");
        setShowDialog(true);
      }
    } catch (err) {
      setDialogMessage("Error publishing post.");
      setShowDialog(true);
    }
  };


  // -------------------------------------------------------
  // Immediate reset on find filter change (prevents stale list flash)
  // -------------------------------------------------------
  useEffect(() => {
    if (activeTab !== "find") return;

    // Invalidate any in-flight result from previous filter
    latestRequestRef.current = Date.now();

    setListings([]);
    setIncluded([]);
    setHasMore(true);
    setPage(1);
    setFilterChanged(true);
    inFlightPageRef.current = null;
    lastLoadedPageRef.current = 0;
    pagingLockRef.current = false;
  }, [activeTab, findFilter]);

  // -------------------------------------------------------
  // Debounce search change only
  // -------------------------------------------------------
  const DEBOUNCE_MS = 350;
  const filterSearchTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (activeTab !== "find") return;

    if (filterSearchTimeoutRef.current)
      clearTimeout(filterSearchTimeoutRef.current);

    filterSearchTimeoutRef.current = setTimeout(() => {
      setHasMore(true);
      setPage(1);
      setFilterChanged(true);
      inFlightPageRef.current = null;
      lastLoadedPageRef.current = 0;
      pagingLockRef.current = false;
    }, DEBOUNCE_MS);

    return () => {
      if (filterSearchTimeoutRef.current)
        clearTimeout(filterSearchTimeoutRef.current);
    };
  }, [searchQuery, activeTab]);


  // -------------------------------------------------------
  // Throttled network requests + cancel stale results
  // -------------------------------------------------------
  const THROTTLE_MS = 500;
  const lastListingsRequestRef = useRef(0);
  const latestRequestRef = useRef(0);

  const waitForThrottle = async () => {
    const now = Date.now();
    const elapsed = now - lastListingsRequestRef.current;
    if (elapsed < THROTTLE_MS) {
      await new Promise((resolve) =>
        setTimeout(resolve, THROTTLE_MS - elapsed)
      );
    }
    lastListingsRequestRef.current = Date.now();
  };

const dedupeById = (arr: any[]) => {
  const map = new Map<string, any>();
  arr.forEach((item) => {
    map.set(String(item.id), item);
  });
  return Array.from(map.values());
};

  // -------------------------------------------------------
  // Load listings
  // -------------------------------------------------------
  const loadListings = async (
    pageNo: number,
    opts?: { reset?: boolean; filterSnapshot?: typeof findFilter; searchSnapshot?: string }
  ) => {
    const { reset = false } = opts || {};
    const filterSnapshot = opts?.filterSnapshot ?? findFilter;
    const searchSnapshot = opts?.searchSnapshot ?? searchQuery;

    if (!reset && (loading || !hasMore)) return;
    if (!reset && inFlightPageRef.current === pageNo) return;
    if (!reset && pageNo <= lastLoadedPageRef.current) return;
    if (reset) {
      inFlightPageRef.current = null;
      lastLoadedPageRef.current = 0;
    }
    inFlightPageRef.current = pageNo;

    setLoading(true);
    await waitForThrottle();

    const token = Date.now();
    latestRequestRef.current = token;

    try {
      // Swimmer listing
      if (filterSnapshot === "swimmers") {
        const uid =
          localStorage.getItem("selected_swimmer_id") ||
          localStorage.getItem("current_user_swimmer_id");

        const userId = Number(uid) || 0;

        const swimmers = await repo.findSwimmers(
          userId,
          0,
          searchSnapshot,
          pageNo
        );

        if (latestRequestRef.current !== token) return;

       setListings((prev) => {
          const merged = pageNo === 1 || reset
            ? swimmers
            : [...prev, ...swimmers];

          return dedupeById(merged);
        });


        if (swimmers.length === 0) setHasMore(false);
        else lastLoadedPageRef.current = Math.max(lastLoadedPageRef.current, pageNo);

        return;
      }

      // Coaches / products listing
      const type = filterSnapshot === "products" ? "product" : "service";

      const params = {
        pub_listingType: type,
        keywords: searchSnapshot || "",
        page: pageNo.toString(),
        per_page: "20",
      };

      const response = await repo.listingsQuery(params, [
        "author.profileImage",
        "images",
      ]);

      if (latestRequestRef.current !== token) return;

      const data = response.data || [];
      const inc = response.included || [];

      if (pageNo === 1 || reset) {
        setListings(data);
        setIncluded(inc);
      } else {
        setListings((p) => [...p, ...data]);
        setIncluded((p) => [...p, ...inc]);
      }

      if (data.length === 0) setHasMore(false);
      else lastLoadedPageRef.current = Math.max(lastLoadedPageRef.current, pageNo);
    } catch (e) {
      console.error("Failed to load listings:", e);
    } finally {
      if (inFlightPageRef.current === pageNo) {
        inFlightPageRef.current = null;
      }
      if (latestRequestRef.current === token) {
        setLoading(false);
      }
    }
  };


  // -------------------------------------------------------
  // Trigger load when filterChanged or page changes
  // -------------------------------------------------------
  useEffect(() => {
    if (activeTab !== "find") return;

    if (filterChanged && !loading) {
      setFilterChanged(false);
      loadListings(1, {
        reset: true,
        filterSnapshot: findFilter,
        searchSnapshot: searchQuery,
      });
      return;
    }

    if (page > 1 && !loading && !filterChanged) {
      loadListings(page);
    }
  }, [filterChanged, page, activeTab, loading, findFilter, searchQuery]);


  // -------------------------------------------------------
  // Safe infinite scroll
  // -------------------------------------------------------
  useEffect(() => {
    if (!loading) {
      pagingLockRef.current = false;
    }
  }, [loading]);

  useEffect(() => {
    if (activeTab !== "find") return;

    const observer = new IntersectionObserver((entries) => {
      const first = entries[0];
      if (
        first.isIntersecting &&
        hasMore &&
        !loading &&
        !filterChanged &&
        latestRequestRef.current !== 0 &&
        !pagingLockRef.current
      ) {
        pagingLockRef.current = true;
        setPage((prev) => prev + 1);
      }
    }, { rootMargin: "200px 0px 200px 0px" });

    const el = loaderRef.current;
    if (el) observer.observe(el);

    return () => {
      if (el) observer.unobserve(el);
    };
  }, [activeTab, hasMore, loading, filterChanged]);


  // -------------------------------------------------------
  // Warning toast for extremely fast switching
  // -------------------------------------------------------
  const [listWarningShown, setListWarningShown] = useState(false);

  useEffect(() => {
    const warn =
      activeTab === "find" &&
      listings.length === 0 &&
      !loading &&
      !hasMore;

    if (warn && !listWarningShown) {
      setListWarningShown(true);

      window.dispatchEvent(
        new CustomEvent("global-toast", {
          detail: {
            message: "Please wait a few seconds… loading results.",
            type: "warning",
          },
        })
      );

      setTimeout(() => setListWarningShown(false), 2500);
    }
  }, [activeTab, listings, loading, hasMore, listWarningShown]);


  // -------------------------------------------------------
  // Add connection
  // -------------------------------------------------------
  const handleAddUser = async (swimmer: any) => {
    try {
      const token = localStorage.getItem("auth_token");
      if (!token) {
        setDialogMessage("You must be logged in.");
        setShowDialog(true);
        return;
      }

      const mySwimmerId = Number(localStorage.getItem("current_user_swimmer_id"));
      const myCoachId = Number(localStorage.getItem("coach_id"));
      const friendId = Number(swimmer.id);

      let response = null;

      if (userType === "swimmer") {
        response = await repo.swimmerConnectToSwimmer(mySwimmerId, friendId);
      } else {
        response = await repo.coachConnectToSwimmer(myCoachId, friendId);
      }

      if (response?.status === 1) {
        setDialogMessage("Connected successfully.");
      } else if (response?.status === 2) {
        setDialogMessage(response.msg || "Connection already exists.");
      } else {
        setDialogMessage(response.msg || "Failed to connect.");
      }

      setShowDialog(true);
    } catch (err) {
      setDialogMessage("Something went wrong while connecting.");
      setShowDialog(true);
    }
  };

  useEffect(() => {
    if (activeTab === "find") setSearchQuery("");
  }, [findFilter, activeTab]);

  const extractSharetribeErrorCode = (response: any): string | null => {
    return (
      response?.code ||
      response?.data?.code ||
      response?.error?.code ||
      response?.errors?.[0]?.code ||
      null
    );
  };

  const mapCoachBookingError = (code: string | null, fallback: string): string => {
    switch (code) {
      case "transaction-payment-failed":
        return "Payment information is invalid or the payment was rejected.";
      case "transaction-provider-banned-or-deleted":
        return "The coach account is banned or deleted.";
      case "transaction-customer-banned-or-deleted":
        return "Your account is banned or deleted.";
      case "transaction-booking-end-is-in-the-past":
      case "transaction-booking-start-before-end":
        return "Selected booking dates are invalid.";
      case "transaction-listing-not-found":
        return "Selected coach listing cannot be found.";
      case "transaction-booking-time-not-available":
        return "The requested booking time is not available.";
      default:
        return fallback;
    }
  };

  const getDefaultStripePaymentMethod = async (): Promise<{
    paymentMethodId: string | null;
    card: any | null;
  }> => {
    const result = await repo.sharedTribecurrentUserShow(false, false, true);
    const includedItems = result?.included || [];

    const stripePaymentMethod = includedItems.find(
      (item: any) => item?.type === "stripePaymentMethod"
    );

    return {
      paymentMethodId: stripePaymentMethod?.attributes?.stripePaymentMethodId || null,
      card: stripePaymentMethod?.attributes?.card || null,
    };
  };

  const handleBuyCoach = async (coach: CoachProfileData) => {
    if (coachBuying) return;
    if (userType === "coach") {
      setDialogMessage("Only swimmers can buy coach sessions.");
      setShowDialog(true);
      return;
    }

    if (!navigator.onLine) {
      setDialogMessage("No internet connection. Please try again.");
      setShowDialog(true);
      return;
    }

    try {
      setCoachBuying(true);

      const { paymentMethodId, card } = await getDefaultStripePaymentMethod();
      if (!paymentMethodId) {
        setDialogMessage("Please add a payment method and try again.");
        setShowDialog(true);
        return;
      }

      const coachPrice = Number(coach.price || 0);
      if (coachPrice <= 0) {
        setDialogMessage("Invalid coach price. Please try again later.");
        setShowDialog(true);
        return;
      }

      setSelectedCoach(coach);
      setCoachPaymentMethodId(paymentMethodId);
      setCoachPaymentMethod(card);
      setProfileOpen(false);
      setCoachCheckoutOpen(true);
    } catch (error) {
      setDialogMessage("Something went wrong. Please try again later.");
      setShowDialog(true);
    } finally {
      setCoachBuying(false);
    }
  };

  const handleConfirmCoachBuy = async () => {
    if (coachBuying || !selectedCoach || !coachPaymentMethodId) return;

    if (!navigator.onLine) {
      setDialogMessage("No internet connection. Please try again.");
      setShowDialog(true);
      return;
    }

    try {
      setCoachBuying(true);

      const listingId = selectedCoach.id;
      const coachPrice = Number(selectedCoach.price || 0);
      const amountInCents = Math.round(coachPrice * 100);
      const currency = (selectedCoach.currency || "USD").toUpperCase();
      const providerCommissionPercentage = -6.5;
      const days = 1;

      const today = new Date();
      const startDateUTC = new Date(
        Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), today.getUTCDate(), 0, 0, 0, 0)
      );
      const endDateUTC = new Date(
        Date.UTC(
          today.getUTCFullYear(),
          today.getUTCMonth(),
          today.getUTCDate() + 30,
          0,
          0,
          0,
          0
        )
      );

      const booking = {
        code: "line-item/day",
        quantity: days,
        reversal: false,
        unitPrice: { amount: amountInCents, currency },
        lineTotal: { amount: amountInCents * days, currency },
        includeFor: ["customer", "provider"],
      };

      const providerCommission = {
        code: "line-item/provider-commission",
        percentage: providerCommissionPercentage,
        reversal: false,
        unitPrice: { amount: amountInCents * days, currency },
        lineTotal: {
          amount: Math.floor((amountInCents * days * providerCommissionPercentage) / 100),
          currency,
        },
        includeFor: ["provider"],
      };

      const initiateResponse = await repo.transactionsInitiate(
        "transition/request-payment",
        {
          listingId,
          bookingStart: startDateUTC.toISOString(),
          bookingEnd: endDateUTC.toISOString(),
          paymentMethod: coachPaymentMethodId,
          seats: 1,
          lineItems: [booking, providerCommission],
        },
        repo.serviceProcessAlias
      );

      const initiateErrorCode = extractSharetribeErrorCode(initiateResponse);
      if (initiateErrorCode || !initiateResponse?.data?.id) {
        setDialogMessage(
          mapCoachBookingError(
            initiateErrorCode,
            "Book request failed. There was an error in the transaction process."
          )
        );
        setShowDialog(true);
        return;
      }

      const transactionId = initiateResponse.data.id;
      const confirmResponse = await repo.transactionsTransition(
        transactionId,
        "transition/confirm-payment",
        null
      );

      const confirmErrorCode = extractSharetribeErrorCode(confirmResponse);
      if (confirmErrorCode || !confirmResponse?.data?.id) {
        setDialogMessage(
          mapCoachBookingError(
            confirmErrorCode,
            "Book request failed. Payment confirmation did not complete."
          )
        );
        setShowDialog(true);
        return;
      }

      setCoachCheckoutOpen(false);
      setDialogMessage(`${selectedCoach.name} has been notified about your booking request.`);
      setShowDialog(true);
    } catch (error) {
      setDialogMessage("Something went wrong. Please try again later.");
      setShowDialog(true);
    } finally {
      setCoachBuying(false);
    }
  };

// -------------------------------------------------------
// Render
// -------------------------------------------------------

return (    
    <div className="space-y-6">
      <h1 className="text-2xl font-semibold border-b pb-3">
        {tabTitles[activeTab] || "Share"}
      </h1>

      {activeTab === "team" && <TeamsSection />}

      {activeTab === "feed" && (
        <div className="relative mt-4">
          <FeedList
            currentUserId={current_user_id}
            reloadKey={forceReloadFeed}
            onAddPost={() => setShowPostDialog(true)}
          />

          <AddPostDialog
            open={showPostDialog}
            onOpenChange={setShowPostDialog}
            onSubmit={handleCreatePost}
          />
        </div>
      )}

      {activeTab === "find" && (
        <>
          <div className="flex space-x-2">
            <Button
              variant={findFilter === "coaches" ? "default" : "outline"}
              onClick={() => setFindFilter("coaches")}
            >
              Coaches
            </Button>

            <CoachProfile
              open={profileOpen}
              onOpenChange={setProfileOpen}
              coach={selectedCoach}
              canBuyCoach={userType !== "coach"}
              onBuyCoach={handleBuyCoach}
              isBuyingCoach={coachBuying}
            />

            <Button
              variant={findFilter === "products" ? "default" : "outline"}
              onClick={() => setFindFilter("products")}
            >
              Products
            </Button>

            <Button
              variant={findFilter === "swimmers" ? "default" : "outline"}
              onClick={() => setFindFilter("swimmers")}
            >
              Swimmers
            </Button>
          </div>

            <div className="mt-4">
              <Input
                placeholder={`Search ${findFilter}...`}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>

          <div className="mt-4 space-y-4">
            {loading && listings.length === 0 && (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary"></div>
              </div>
            )}


            {findFilter === "swimmers" &&
              dedupeById(listings).map((s) => (
                  <SwimmerCard
                    key={s.id}
                    swimmer={s}
                    connectedIds={connectedIds}
                    onAdd={handleAddUser}
                  />
                ))}


            {findFilter === "coaches" &&
              listings.map(
                (l) =>
                  l.attributes && (
                    <CoachCard
                        key={l.id}
                        listing={l}
                        included={included}
                        onViewProfile={(coach: CoachProfileData) => {
                        setSelectedCoach(coach);
                        setProfileOpen(true);
                      }}
                      />
                  )
              )}

            {findFilter === "products" &&
              listings.map(
                (l) =>
                  l.attributes && (
                    <ProductCard key={l.id} listing={l} included={included} />
                  )
              )}

            <div ref={loaderRef} className="h-14 flex items-center justify-center">
              {loading && listings.length > 0 && (
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
              )}
            </div>
          </div>
        </>
      )}

      {/* {activeTab === "messages" && <MessagesPage />} */}
        {activeTab === "messages" && (
          <MessagesPage key="messages-root" />
        )}

      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-sm">
          <DialogTitle>Notification</DialogTitle>
          <p className="text-muted-foreground mt-2">{dialogMessage}</p>

          <div className="mt-4 flex justify-end">
            <Button onClick={() => setShowDialog(false)}>OK</Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={coachCheckoutOpen} onOpenChange={setCoachCheckoutOpen}>
        <DialogContent className="max-w-md w-[calc(100vw-2rem)] max-h-[85vh] overflow-y-auto overflow-x-hidden">
          <DialogTitle>Coach Checkout</DialogTitle>

          <div className="mt-2 space-y-4">
            <div>
              <p className="text-sm text-muted-foreground">Coach</p>
              <p className="font-semibold break-all [overflow-wrap:anywhere]">
                {selectedCoach?.name || "Coach"}
              </p>
            </div>

            <div className="border rounded-md p-4">
              <p className="font-medium mb-2">Order Breakdown</p>
              <div className="flex justify-between text-sm mb-1">
                <span>Session Price</span>
                <span>
                  {(selectedCoach?.currency || "USD").toUpperCase()} {selectedCoach?.price ?? 0}
                </span>
              </div>
              <div className="flex justify-between font-semibold text-base mt-2 border-t pt-2">
                <span>Total</span>
                <span>
                  {(selectedCoach?.currency || "USD").toUpperCase()} {selectedCoach?.price ?? 0}
                </span>
              </div>
            </div>

            <div>
              <p className="font-medium mb-2">Pay with</p>
              {!coachPaymentMethodId ? (
                <p className="text-red-600 text-sm">No payment method found. Please add card.</p>
              ) : (
                <div className="flex items-center gap-2 border rounded-md p-3">
                  <CreditCard className="h-5 w-5" />
                  <span>
                    {coachPaymentMethod?.brand?.toUpperCase() || "CARD"} ••••{" "}
                    {coachPaymentMethod?.last4 || ""}
                  </span>
                </div>
              )}
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setCoachCheckoutOpen(false)}>
                Cancel
              </Button>
              <Button
                disabled={coachBuying || !coachPaymentMethodId}
                onClick={handleConfirmCoachBuy}
              >
                {coachBuying ? "Processing..." : "Confirm Pay"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>


      
    </div>
  );
}
